import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import routeConfiguration, { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Icons,
} from '../../../components';
import { createResourceLocatorString } from '../../../util/routes';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import { SIGNUP } from '../../../util/enums';
import { getCategories } from '../../../config/runtimeListingConfig';
import { getCategoryUrl } from '../../../util/categoryLandingHelpers';

import css from './TopbarDesktop.module.css';
import { useMyContextFunctions } from '../../../context/ContextFunctions';
import { useMyContext } from '../../../context/StateHolder';
import { useSelector } from 'react-redux';
import { selectWishlistIds } from '../../../ducks/user.duck';
import { getListingCategories } from '../../../ducks/hostedAssets.duck';

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    history,
    updatedCartCount,
    notificationCount,
  } = props;
  const listingCategories = useSelector(getListingCategories);
  const [mounted, setMounted] = useState(false);
  const [showSearchOverlay, setSearchOverlay] = useState(false);
  const { onOpenAuthModal, onSetAuthType } = useMyContextFunctions();
  const { openAuthModal } = useMyContext();

  // Wishlist count: profile publicData for users, localStorage mirror for guests
  const wishlistCount = useSelector(selectWishlistIds).length;

  // Helper function to build category query strings
  const buildCategoryQuery = (level1, level2, level3) => {
    const params = new URLSearchParams();
    if (level1) params.append('pub_categoryLevel1', level1);
    if (level2) params.append('pub_categoryLevel2', level2);
    if (level3) params.append('pub_categoryLevel3', level3);
    return params.toString();
  };

  // Helper function to get landing page URL for a P1 category
  const getCategoryLandingUrl = category => {
    // Only generate landing URLs for P1 (level 1) categories
    if (category.level === 1 || !category.subcategories) {
      // Find the matching category in config to get the correct url_slug
      const configCategory = getCategories().find(c => c.id === category.id);
      if (configCategory && configCategory.url_slug) {
        return `/${configCategory.url_slug}`;
      }
    }
    return null;
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const location = useLocation();
  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const profileMenu = authenticatedOnClientSide ? (
    <Menu contentPosition="left">
      <MenuLabel
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.accountMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink placeholder="person" />
        <svg className={css.avatarChevron} viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </MenuLabel>
      <MenuContent rootClassName={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="OffersPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('OffersPage'))}
            name="OffersPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourOffers" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <button
      type="button"
      onClick={() => {
        onOpenAuthModal(!openAuthModal);
        onSetAuthType(SIGNUP);
      }}
      className={css.iconButton}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.logIn' })}
      title={intl.formatMessage({ id: 'TopbarDesktop.logIn' })}
    >
      <Icons name="user" />
    </button>
  );

  const renderLogo = (
    <LinkedLogo
      className={css.logoLink}
      layout="desktop"
      alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
    />
  );

  const redirectToPage = (name, params = {}, search = {}) => {
    history.push(createResourceLocatorString(name, routeConfiguration(), params, search));
  };

  const renderNavBarWithoutOverlay = (
    <div>
      <nav className={classes}>
        <div className={css.fixedWidthContainer}>
          {renderLogo}
          <div className={css.middleLinks}>
            {(listingCategories || []).map((category, index) => {
              const landingUrl = getCategoryLandingUrl(category);

              if (landingUrl) {
                // The category whose page (or a page under it) is open is marked
                const isCurrent =
                  location.pathname === landingUrl ||
                  location.pathname.startsWith(`${landingUrl}/`);
                return (
                  <Link
                    key={index}
                    className={classNames(css.navLink, { [css.navLinkCurrent]: isCurrent })}
                    to={landingUrl}
                    aria-current={isCurrent ? 'page' : undefined}
                  >
                    {category.name}
                  </Link>
                );
              }

              return (
                <NamedLink
                  key={index}
                  className={css.navLink}
                  name="SearchPage"
                  to={{ search: buildCategoryQuery(category.id) }}
                >
                  {category.name}
                </NamedLink>
              );
            })}
          </div>
          <div className={css.rightLinks}>
            {/* Sell leads the group, so it never sits between the icons (guest or member) */}
            <NamedLink name="NewListingPage" className={css.sellButton}>
              <FormattedMessage id="TopbarDesktop.sell" />
            </NamedLink>
            {[
              {
                name: 'search',
                label: intl.formatMessage({ id: 'TopbarDesktop.search' }),
                onClick: () => setSearchOverlay(true),
              },
              {
                name: 'unfavorite',
                count: wishlistCount,
                label: intl.formatMessage(
                  { id: 'TopbarDesktop.favorites' },
                  { count: wishlistCount }
                ),
                // FavoritesPage is a public route; guests carry a localStorage wishlist
                onClick: () => redirectToPage('FavoritesPage'),
              },
              ...(isAuthenticated
                ? [
                    {
                      name: 'email',
                      count: notificationCount,
                      // Red: something new is waiting (the cart's green badge is just a count)
                      alert: true,
                      label: intl.formatMessage(
                        { id: 'TopbarDesktop.inbox' },
                        { count: Number(notificationCount) || 0 }
                      ),
                      onClick: () =>
                        redirectToPage('InboxPage', {
                          tab: currentUserHasListings ? 'sales' : 'orders',
                        }),
                    },
                  ]
                : []),
              {
                name: 'cart',
                count: updatedCartCount,
                label: intl.formatMessage(
                  { id: 'TopbarDesktop.cart' },
                  { count: Number(updatedCartCount) || 0 }
                ),
                // CartPage is a public route; guests carry a localStorage cart
                onClick: () => redirectToPage('CartPage'),
              },
            ].map(icon => (
              <button
                key={icon.name}
                type="button"
                onClick={icon.onClick}
                className={css.iconButton}
                aria-label={icon.label}
                title={icon.label}
              >
                <Icons name={icon.name} />
                {icon.count && Number(icon.count) > 0 ? (
                  <span
                    className={classNames(css.badge, { [css.badgeAlert]: icon.alert })}
                    aria-hidden="true"
                  >
                    {icon.count}
                  </span>
                ) : null}
              </button>
            ))}
            {signupLink}
            {profileMenu}
          </div>
        </div>
      </nav>
    </div>
  );

  const renderNavBarWithOverlay = (
    // Takeover (prototype Home search state): the whole topbar row becomes
    // magnifier + borderless input + close; opens pre-focused (ledger §4)
    <nav className={classes}>
      <div className={css.takeoverRow}>
        <span className={css.takeoverSearchIcon}>
          <Icons name="navSearch" />
        </span>
        <TopbarSearchForm
          className={css.takeoverForm}
          onSubmit={onSearchSubmit}
          initialValues={initialSearchFormValues}
          appConfig={appConfig}
          autoFocus
          takeover
        />
        <button
          type="button"
          onClick={() => setSearchOverlay(false)}
          className={css.takeoverClose}
          aria-label={intl.formatMessage({ id: 'TopbarDesktop.closeSearch' })}
        >
          <Icons name="closeIcon" />
        </button>
      </div>
    </nav>
  );

  return showSearchOverlay ? renderNavBarWithOverlay : renderNavBarWithoutOverlay;
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
